import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { useI18n } from '@/lib/i18n';

export default function VerifyEmail() {
  const { language } = useI18n();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  const tr = language === 'tr';

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setInterval(() => setWait((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [wait]);

  async function verify(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await api.post('/auth/verify-email', { email: email.trim(), code });
      setVerified(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : tr ? 'Sunucuya ulaşılamadı.' : 'The server could not be reached.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (wait > 0 || !email.trim()) return;
    setError(null);
    setInfo(null);
    setResendBusy(true);
    try {
      await api.post('/auth/resend-verification', { email: email.trim() });
      setWait(60);
      setCode('');
      setInfo(tr ? 'Yeni kod gönderildi. Önceki kod artık geçersiz.' : 'A new code was sent. The previous code is no longer valid.');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : tr ? 'Kod gönderilemedi.' : 'The code could not be sent.');
    } finally {
      setResendBusy(false);
    }
  }

  if (verified) return <>
    <div><h1 className="auth__title">{tr ? 'E-posta doğrulandı' : 'Email verified'}</h1><p className="auth__sub">{tr ? 'Kurum başvurunuza güvenle devam edebilirsiniz.' : 'You can now continue with your organisation application.'}</p></div>
    <Link className="btn btn--primary btn--lg auth__wide-action" to="/giris">{tr ? 'Giriş yap' : 'Sign in'}</Link>
  </>;

  return <>
    <div><h1 className="auth__title">{tr ? 'E-postanızı doğrulayın' : 'Verify your email'}</h1><p className="auth__sub">{tr ? 'E-postanıza gönderilen altı haneli kodu girin. Kod 10 dakika geçerlidir.' : 'Enter the six-digit code sent to your email. The code is valid for 10 minutes.'}</p></div>
    <form className="auth__form" onSubmit={verify} noValidate>
      {error && <div className="auth__error" role="alert">{error}</div>}
      {info && <div className="auth__ok" role="status">{info}</div>}
      <Field label={tr ? 'E-posta' : 'Email'} required>{({ id, describedBy }) => <Input id={id} type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required aria-describedby={describedBy} />}</Field>
      <Field label={tr ? 'Doğrulama kodu' : 'Verification code'} hint={tr ? 'Örnek: 024815' : 'Example: 024815'} required>{({ id, describedBy }) => <Input id={id} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" required pattern="[0-9]{6}" maxLength={6} aria-describedby={describedBy} className="auth__verification-code" autoFocus />}</Field>
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={code.length !== 6}>{tr ? 'E-postamı doğrula' : 'Verify my email'}</Button>
      <Button type="button" variant="ghost" size="md" loading={resendBusy} disabled={wait > 0 || !email.trim()} onClick={() => void resend()}>{wait > 0 ? (tr ? `${wait} sn sonra yeniden gönder` : `Resend in ${wait}s`) : (tr ? 'Yeni kod gönder' : 'Send a new code')}</Button>
    </form>
    <p className="small muted">{tr ? 'Beş hatalı denemeden sonra kod kilitlenir; yeni kod isteyebilirsiniz.' : 'The code locks after five failed attempts; you can request a new one.'}</p>
  </>;
}
